import { useState, useEffect, type FormEvent } from 'react'
import { FiEdit2 } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import type { Language } from '../i18n/index'
import { useAuth } from '../contexts/AuthContext'
import { users, sessions, subscriptions, professional as proApi, support, statsApi, type FullUser, type GameSession, type SubscriptionInfo, type AdvancedStats } from '../api/client'
import StatsPreviewCard from './stats/StatsPreviewCard'
import Footer from './Footer'

interface UserPageProps {
  onBack:             () => void
  onGoToSubscription: () => void
  onGoToStats:        () => void
}

const TIER_META = {
  BASIC:   { emoji: '🌱', gradient: 'linear-gradient(135deg, #52B788, #2D6A4F)', text: '#FFFFFF' },
  PLUS:    { emoji: '🌿', gradient: 'linear-gradient(135deg, #D4A017, #B5841A)', text: '#FFFFFF' },
  PREMIUM: { emoji: '🌳', gradient: 'linear-gradient(135deg, #1B4332, #2D6A4F)', text: '#FFFFFF' },
}

const ACTIVITY_ICONS: Record<string, string> = {
  memory: '🃏', wordmatch: '📖', cutlery: '🍴', numbers: '🔢', words: '✍️',
}

export default function UserPage({ onBack, onGoToSubscription, onGoToStats }: UserPageProps) {
  const { t, language, setLanguage } = useTranslation()
  const { user: authUser, updateUser } = useAuth()
  const p = t.profile

  const [fullUser,       setFullUser]       = useState<FullUser | null>(null)
  const [recentSessions, setRecentSessions] = useState<GameSession[]>([])
  const [subscription,   setSubscription]   = useState<SubscriptionInfo | null>(null)
  const [advancedStats,  setAdvancedStats]  = useState<AdvancedStats | null>(null)
  const [loading,        setLoading]        = useState(true)

  // Inline name edit
  const [editing,   setEditing]   = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [saving,    setSaving]    = useState(false)

  // Professional mode toggle
  const [disablingPro, setDisablingPro] = useState(false)
  const [enablingPro,  setEnablingPro]  = useState(false)

  // Help / support form (in modal)
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [helpName,    setHelpName]    = useState('')
  const [helpMessage, setHelpMessage] = useState('')
  const [helpSending, setHelpSending] = useState(false)
  const [helpStatus,  setHelpStatus]  = useState<'idle' | 'success' | 'error'>('idle')

  // Recent sessions expand
  const [showAllSessions, setShowAllSessions] = useState(false)

  useEffect(() => {
    Promise.all([
      users.me(),
      sessions.list({ limit: 50 }),
      subscriptions.me().catch(() => null),
      statsApi.advanced().catch(() => null),
    ]).then(([me, sess, sub, adv]) => {
      setFullUser(me)
      setRecentSessions(sess.items)
      setSubscription(sub)
      setAdvancedStats(adv)
      setHelpName(me.displayName ?? '')
    }).finally(() => setLoading(false))
  }, [])

  const startEdit = () => {
    setNameInput(fullUser?.displayName ?? authUser?.displayName ?? '')
    setEditing(true)
  }

  const cancelEdit = () => setEditing(false)

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = nameInput.trim()
    if (!trimmed) return
    setSaving(true)
    try {
      await users.update({ displayName: trimmed })
      setFullUser((prev) => prev ? { ...prev, displayName: trimmed } : prev)
      updateUser({ displayName: trimmed })
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const disableProfessional = async () => {
    setDisablingPro(true)
    try {
      await proApi.disable()
      updateUser({ isProfessional: false })
    } catch {
      // ignore
    } finally {
      setDisablingPro(false)
    }
  }

  const enableProfessional = async () => {
    setEnablingPro(true)
    try {
      await proApi.enable()
      updateUser({ isProfessional: true })
    } catch {
      // ignore
    } finally {
      setEnablingPro(false)
    }
  }

  const openHelpModal = () => {
    setHelpStatus('idle')
    setHelpMessage('')
    setHelpModalOpen(true)
  }

  const closeHelpModal = () => {
    setHelpModalOpen(false)
    setHelpStatus('idle')
  }

  const submitHelp = async (e: FormEvent) => {
    e.preventDefault()
    const trimmedName    = helpName.trim()
    const trimmedMessage = helpMessage.trim()
    if (!trimmedName || !trimmedMessage) return
    setHelpSending(true)
    setHelpStatus('idle')
    try {
      await support.send({
        name:      trimmedName,
        message:   trimmedMessage,
        userTier:  fullUser?.tier ?? authUser?.tier,
        expiresAt: subscription?.expiresAt ?? null,
      })
      setHelpStatus('success')
      setHelpMessage('')
    } catch {
      setHelpStatus('error')
    } finally {
      setHelpSending(false)
    }
  }

  const displayName = fullUser?.displayName ?? authUser?.displayName
  const initial     = (displayName ?? authUser?.email ?? '?')[0].toUpperCase()
  const tier        = (fullUser?.tier ?? authUser?.tier ?? 'BASIC') as 'BASIC' | 'PLUS' | 'PREMIUM'
  const tierMeta    = TIER_META[tier]

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
    }).format(new Date(iso))

  const activityLabel = (slug: string) =>
    t.dashboard.games[slug as keyof typeof t.dashboard.games]?.label ?? slug

  const modeLabel = (mode: string | null) =>
    mode ? (t.memory.modes[mode as keyof typeof t.memory.modes]?.label ?? mode) : null

  const diffLabel = (diff: string) =>
    t.memory.difficulty[diff as keyof typeof t.memory.difficulty]?.label ?? diff

  return (
    <div style={s.root}>
      {/* ── Hero Header ─────────────────────────────────────────── */}
      <header style={s.hero}>
        <div style={s.heroInner}>
          <button onClick={onBack} style={s.backBtn} aria-label={p.backBtn}>
            {p.backBtn}
          </button>

          <div style={s.heroBody}>
            {/* Avatar */}
            <div style={s.avatar} aria-hidden>
              {initial}
            </div>

            {/* Name + edit */}
            <div style={s.heroInfo}>
              {editing ? (
                <form onSubmit={saveEdit} style={s.editForm}>
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    disabled={saving}
                    style={s.editInput}
                    maxLength={80}
                    aria-label={p.editName}
                  />
                  <div style={s.editBtns}>
                    <button type="submit" disabled={saving || !nameInput.trim()} style={s.editSaveBtn}>
                      {saving ? '…' : p.saveName}
                    </button>
                    <button type="button" onClick={cancelEdit} style={s.editCancelBtn}>
                      {p.cancelEdit}
                    </button>
                  </div>
                </form>
              ) : (
                <div style={s.nameRow}>
                  <h1 style={s.heroName}>
                    {displayName ?? authUser?.email?.split('@')[0]}
                  </h1>
                  <button onClick={startEdit} style={s.editIconBtn} aria-label={p.editName} title={p.editName}>
                    <FiEdit2 size={16} />
                  </button>
                </div>
              )}

              <p style={s.heroEmail}>{fullUser?.email ?? authUser?.email}</p>

              {/* Tier badge */}
              <div style={{ ...s.tierBadge, background: tierMeta.gradient, color: tierMeta.text }}>
                {tierMeta.emoji}&nbsp;{p.tierNames[tier]}
              </div>
            </div>
          </div>
        </div>

        {/* Decorative circles */}
        <div style={s.heroDecor1} aria-hidden />
        <div style={s.heroDecor2} aria-hidden />
      </header>

      <main style={s.main}>
        {/* ── Stats Preview ─────────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{p.statsTitle}</h2>
          <StatsPreviewCard
            avgScore={advancedStats?.avgScore ?? null}
            bestActivity={advancedStats?.bestActivity ? activityLabel(advancedStats.bestActivity.slug) : null}
            totalSessions={advancedStats?.totalSessions ?? 0}
            onViewStats={onGoToStats}
            loading={loading}
            avgScoreLabel={t.stats.avgScore}
            bestLabel={t.stats.bestActivity}
            sessionsLabel={t.stats.totalSessions}
            ctaLabel={t.stats.viewStats}
          />
        </section>

        {/* ── Recent Sessions ────────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{p.sessionsTitle}</h2>

          {loading ? (
            <div style={s.sessionsList}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton" style={{ height: 72, borderRadius: 12, marginBottom: 10 }} />
              ))}
            </div>
          ) : recentSessions.length === 0 ? (
            <p style={s.emptyMsg}>{p.noSessions}</p>
          ) : (
            <div style={s.sessionsList}>
              {recentSessions.slice(0, showAllSessions ? 10 : 3).map((sess, i) => (
                <div
                  key={sess.id}
                  className="fade-up"
                  style={{ ...s.sessionItem, animationDelay: `${i * 0.05}s` }}
                >
                  {/* Icon */}
                  <div style={s.sessionIcon}>
                    {ACTIVITY_ICONS[sess.activitySlug] ?? '🎮'}
                  </div>

                  {/* Details */}
                  <div style={s.sessionDetails}>
                    <div style={s.sessionTop}>
                      <span style={s.sessionGame}>{activityLabel(sess.activitySlug)}</span>
                      {modeLabel(sess.mode) && (
                        <span style={s.sessionMeta}>{modeLabel(sess.mode)}</span>
                      )}
                      <span style={s.sessionMeta}>{diffLabel(sess.difficulty)}</span>
                    </div>
                    <div style={s.sessionBottom}>
                      {sess.score !== null && (
                        <span style={s.sessionScore}>{sess.score.toLocaleString()} {p.score}</span>
                      )}
                      {sess.moves !== null && (
                        <span style={s.sessionStat}>{sess.moves} {p.moves}</span>
                      )}
                    </div>
                  </div>

                  {/* Right: status + date */}
                  <div style={s.sessionRight}>
                    <div style={{
                      ...s.statusDot,
                      background: sess.status === 'COMPLETED' ? '#52B788' : '#C0AFA0',
                    }} />
                    <span style={s.sessionDate}>
                      {formatDate(sess.completedAt ?? sess.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
              {!showAllSessions && recentSessions.length > 3 && (
                <button
                  onClick={() => setShowAllSessions(true)}
                  style={s.showMoreBtn}
                >
                  •••
                </button>
              )}
            </div>
          )}
        </section>

        {/* ── Account Details ────────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{p.accountTitle}</h2>
          <div style={s.accountCard}>
            <AccountRow label={p.emailLabel}   value={fullUser?.email ?? authUser?.email ?? '—'} />
            <div style={s.accountDivider} />
            <AccountRow label={p.tierLabel}    value={`${tierMeta.emoji} ${p.tierNames[tier]}`} />
            {fullUser?.createdAt && (
              <>
                <div style={s.accountDivider} />
                <AccountRow label={p.memberSince} value={formatDate(fullUser.createdAt)} />
              </>
            )}
          </div>

          <button onClick={onGoToSubscription} style={s.upgradeBtn}>
            {p.viewPlans}
          </button>
        </section>

        {/* ── Professional Mode (active — PREMIUM + enabled) ───── */}
        {authUser?.isProfessional && tier === 'PREMIUM' && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>{p.professionalModeTitle}</h2>
            <div style={s.accountCard}>
              <div style={s.proActiveRow}>
                <div style={s.proActiveDot} aria-hidden />
                <span style={s.proActiveText}>{p.professionalModeActive}</span>
              </div>
              <div style={s.accountDivider} />
              <div style={s.proHintRow}>
                <span style={s.accountLabel}>{p.disableProfessionalHint}</span>
              </div>
              <div style={{ padding: '0 22px 18px' }}>
                <button
                  onClick={disableProfessional}
                  disabled={disablingPro}
                  style={{ ...s.proDisableBtn, opacity: disablingPro ? 0.6 : 1 }}
                >
                  {disablingPro ? '…' : p.disableProfessional}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── Professional Mode enable (PREMIUM but not yet enabled) */}
        {!authUser?.isProfessional && tier === 'PREMIUM' && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>{p.professionalModeTitle}</h2>
            <div style={s.accountCard}>
              <div style={s.proHintRow}>
                <span style={s.accountLabel}>{p.professionalModeTeaserDesc}</span>
              </div>
              <div style={{ padding: '0 22px 18px' }}>
                <button
                  onClick={enableProfessional}
                  disabled={enablingPro}
                  style={{ ...s.upgradeBtn, marginTop: 0, opacity: enablingPro ? 0.6 : 1 }}
                >
                  {enablingPro ? '…' : p.professionalModeTitle + ' →'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── Professional Mode teaser (non-PREMIUM users) ──────── */}
        {tier !== 'PREMIUM' && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>{p.professionalModeTeaser}</h2>
            <div style={s.proTeaserCard}>
              <div style={s.proTeaserIcon} aria-hidden>👩‍⚕️</div>
              <p style={s.proTeaserDesc}>{p.professionalModeTeaserDesc}</p>
              <button onClick={onGoToSubscription} style={s.proTeaserBtn}>
                {p.professionalModeUpgradeBtn}
              </button>
            </div>
          </section>
        )}

        {/* ── Language Settings ──────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{p.languageTitle}</h2>
          <div style={s.langCard}>
            {([['es', p.languageEs], ['en', p.languageEn]] as [Language, string][]).map(([lang, label]) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                style={{
                  ...s.langBtn,
                  ...(language === lang ? s.langBtnActive : {}),
                }}
                aria-pressed={language === lang}
              >
                {label}
                {language === lang && <span style={s.langCheck} aria-hidden>✓</span>}
              </button>
            ))}
          </div>
        </section>

        {/* ── Need Help ──────────────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{p.helpTitle}</h2>
          <button onClick={openHelpModal} style={s.upgradeBtn}>
            {language === 'es' ? 'Contactar' : 'Contact'}
          </button>
        </section>

        {/* ── Contact Modal ──────────────────────────────────────── */}
        {helpModalOpen && (
          <div style={s.modalOverlay} onClick={closeHelpModal}>
            <div style={s.modalBox} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <h2 style={s.modalTitle}>{p.helpTitle}</h2>
                <button onClick={closeHelpModal} style={s.modalCloseBtn} aria-label="Cerrar">✕</button>
              </div>
              {helpStatus === 'success' ? (
                <div style={s.helpSuccess}>
                  <p>{p.helpSuccess}</p>
                  <button onClick={closeHelpModal} style={{ ...s.upgradeBtn, marginTop: 16 }}>
                    {language === 'es' ? 'Cerrar' : 'Close'}
                  </button>
                </div>
              ) : (
                <form onSubmit={submitHelp} style={s.helpForm}>
                  <div style={s.helpField}>
                    <label style={s.helpLabel}>{p.helpName}</label>
                    <input
                      value={helpName}
                      onChange={(e) => setHelpName(e.target.value)}
                      disabled={helpSending}
                      style={s.helpInput}
                      maxLength={120}
                      required
                    />
                  </div>
                  <div style={s.helpField}>
                    <label style={s.helpLabel}>{p.helpMessage}</label>
                    <textarea
                      value={helpMessage}
                      onChange={(e) => setHelpMessage(e.target.value)}
                      disabled={helpSending}
                      style={s.helpTextarea}
                      rows={4}
                      maxLength={5000}
                      required
                    />
                  </div>
                  {helpStatus === 'error' && (
                    <p style={s.helpError}>{p.helpError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={helpSending || !helpName.trim() || !helpMessage.trim()}
                    style={{ ...s.upgradeBtn, marginTop: 0, opacity: (helpSending || !helpName.trim() || !helpMessage.trim()) ? 0.6 : 1 }}
                  >
                    {helpSending ? p.helpSending : p.helpSubmit}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  )
}

/* ── Sub-components ──────────────────────────────────────────── */

function AccountRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={s.accountRow}>
      <span style={s.accountLabel}>{label}</span>
      <span style={s.accountValue}>{value}</span>
    </div>
  )
}

/* ── Styles ──────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:     '100vh',
    display:       'flex',
    flexDirection: 'column',
    background:    '#FAF6EF',
  },

  // ── Hero ──
  hero: {
    position:   'relative',
    background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
    padding:    '28px 28px 44px',
    overflow:   'hidden',
  },
  heroInner: {
    position: 'relative',
    zIndex:   1,
    maxWidth: 760,
    margin:   '0 auto',
  },
  backBtn: {
    display:      'inline-flex',
    alignItems:   'center',
    fontSize:     '0.95rem',
    fontWeight:   700,
    color:        'rgba(255,255,255,0.75)',
    background:   'rgba(255,255,255,0.1)',
    border:       '1px solid rgba(255,255,255,0.18)',
    borderRadius: 10,
    padding:      '9px 16px',
    cursor:       'pointer',
    marginBottom: 28,
    minHeight:    44,
  },
  heroBody: {
    display:    'flex',
    alignItems: 'flex-start',
    gap:        24,
  },
  avatar: {
    width:          80,
    height:         80,
    borderRadius:   '50%',
    background:     'linear-gradient(145deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))',
    border:         '2.5px solid rgba(255,255,255,0.3)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontFamily:     "'Cormorant Garamond', serif",
    fontSize:       '2.4rem',
    fontWeight:     700,
    color:          '#FFFFFF',
    flexShrink:     0,
    boxShadow:      '0 4px 20px rgba(0,0,0,0.2)',
  },
  heroInfo: {
    flex:    1,
    minWidth: 0,
  },
  nameRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
    marginBottom: 6,
  },
  heroName: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    'clamp(1.8rem, 4vw, 2.6rem)',
    fontWeight:  700,
    color:       '#FFFFFF',
    lineHeight:  1.1,
    wordBreak:   'break-word',
  },
  editIconBtn: {
    background:     'rgba(255,255,255,0.12)',
    border:         '1px solid rgba(255,255,255,0.2)',
    borderRadius:   8,
    padding:        '4px 8px',
    cursor:         'pointer',
    color:          'rgba(255,255,255,0.85)',
    minWidth:       36,
    minHeight:      36,
    flexShrink:     0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  heroEmail: {
    fontSize:    '0.95rem',
    color:       'rgba(255,255,255,0.62)',
    fontWeight:  500,
    marginBottom: 12,
    wordBreak:   'break-all',
  },
  tierBadge: {
    display:       'inline-flex',
    alignItems:    'center',
    gap:           4,
    padding:       '6px 14px',
    borderRadius:  20,
    fontSize: '0.875rem',
    fontWeight:    800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    boxShadow:     '0 2px 10px rgba(0,0,0,0.18)',
  },

  // Inline edit form
  editForm: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
    marginBottom:  6,
  },
  editInput: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    '1.8rem',
    fontWeight:  700,
    color:       '#FFFFFF',
    background:  'transparent',
    border:      'none',
    borderBottom: '2px solid rgba(255,255,255,0.5)',
    outline:     'none',
    padding:     '4px 0',
    width:       '100%',
    lineHeight:  1.2,
  },
  editBtns: {
    display: 'flex',
    gap:     10,
  },
  editSaveBtn: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '0.9rem',
    fontWeight:    800,
    color:         '#1B4332',
    background:    '#FFFFFF',
    border:        'none',
    borderRadius:  8,
    padding:       '8px 18px',
    cursor:        'pointer',
    minHeight:     40,
  },
  editCancelBtn: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '0.9rem',
    fontWeight:    700,
    color:         'rgba(255,255,255,0.7)',
    background:    'rgba(255,255,255,0.1)',
    border:        '1px solid rgba(255,255,255,0.2)',
    borderRadius:  8,
    padding:       '8px 18px',
    cursor:        'pointer',
    minHeight:     40,
  },

  heroDecor1: {
    position:     'absolute',
    top:          -80,
    right:        -80,
    width:        260,
    height:       260,
    borderRadius: '50%',
    background:   'rgba(255,255,255,0.04)',
    pointerEvents:'none',
  },
  heroDecor2: {
    position:     'absolute',
    bottom:       -40,
    left:         '30%',
    width:        180,
    height:       180,
    borderRadius: '50%',
    background:   'rgba(255,255,255,0.03)',
    pointerEvents:'none',
  },

  // ── Main content ──
  main: {
    flex:       1,
    maxWidth:   760,
    width:      '100%',
    margin:     '0 auto',
    padding:    '32px 24px 56px',
    display:    'flex',
    flexDirection: 'column',
    gap:        36,
  },
  section: {},
  sectionTitle: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '1.7rem',
    fontWeight:   600,
    color:        '#1B4332',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: '1.5px solid #E3D5C0',
  },

  // ── Sessions ──
  sessionsList: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
  },
  sessionItem: {
    display:      'flex',
    alignItems:   'center',
    gap:          16,
    background:   '#FFFFFF',
    borderRadius: 14,
    padding:      '14px 18px',
    boxShadow:    '0 2px 10px rgba(27,67,50,0.06)',
    border:       '1.5px solid #EFE7D7',
  },
  sessionIcon: {
    width:          48,
    height:         48,
    borderRadius:   12,
    background:     '#F0FAF4',
    border:         '1.5px solid #D8ECD6',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '1.5rem',
    flexShrink:     0,
  },
  sessionDetails: {
    flex:    1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap:     4,
  },
  sessionTop: {
    display:   'flex',
    alignItems: 'center',
    flexWrap:  'wrap',
    gap:       6,
  },
  sessionGame: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    '1.15rem',
    fontWeight:  700,
    color:       '#1B4332',
  },
  sessionMeta: {
    fontSize: '0.875rem',
    fontWeight:   600,
    color:        '#9A8070',
    background:   '#EFE7D7',
    padding:      '2px 8px',
    borderRadius: 20,
  },
  sessionBottom: {
    display:    'flex',
    alignItems: 'center',
    gap:        12,
  },
  sessionScore: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    '1.1rem',
    fontWeight:  700,
    color:       '#B5541A',
  },
  sessionStat: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:     '#9A8070',
  },
  sessionRight: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'flex-end',
    gap:            6,
    flexShrink:     0,
  },
  statusDot: {
    width:        8,
    height:       8,
    borderRadius: '50%',
  },
  sessionDate: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#9A8070',
    whiteSpace: 'nowrap',
  },
  emptyMsg: {
    fontSize:   '1rem',
    color:      '#9A8070',
    fontWeight: 500,
    fontStyle:  'italic',
    padding:    '24px 0',
    textAlign:  'center',
  },

  upgradeBtn: {
    display:      'block',
    width:        '100%',
    marginTop:    16,
    padding:      '16px 24px',
    background:   'linear-gradient(135deg, #52B788, #2D6A4F)',
    color:        '#FFFFFF',
    fontFamily:   'Nunito, sans-serif',
    fontSize:     '1rem',
    fontWeight:   800,
    letterSpacing:'0.03em',
    border:       'none',
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    52,
    boxShadow:    '0 4px 16px rgba(45,106,79,0.28)',
  },

  // ── Account ──
  accountCard: {
    background:   '#FFFFFF',
    borderRadius: 16,
    padding:      '8px 0',
    boxShadow:    '0 2px 14px rgba(27,67,50,0.07)',
    border:       '1.5px solid #EFE7D7',
    overflow:     'hidden',
  },
  accountRow: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '16px 22px',
    gap:             16,
  },
  accountLabel: {
    fontSize: '0.875rem',
    fontWeight:    700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color:         '#9A8070',
    flexShrink:    0,
  },
  accountValue: {
    fontSize:  '1rem',
    fontWeight: 600,
    color:     '#2E1F14',
    textAlign: 'right',
    wordBreak: 'break-all',
  },
  accountDivider: {
    height:     1,
    background: '#EFE7D7',
    margin:     '0 22px',
  },

  // ── Professional Mode (active) ──
  proActiveRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
    padding:    '16px 22px',
  },
  proActiveDot: {
    width:        10,
    height:       10,
    borderRadius: '50%',
    background:   '#52B788',
    flexShrink:   0,
  },
  proActiveText: {
    fontSize:   '1rem',
    fontWeight: 600,
    color:      '#1B4332',
  },
  proHintRow: {
    padding: '12px 22px',
  },
  proDisableBtn: {
    display:      'block',
    width:        '100%',
    padding:      '14px 20px',
    background:   'none',
    border:       '1.5px solid #F5C6C2',
    borderRadius: 12,
    fontSize:     '0.95rem',
    fontWeight:   700,
    color:        '#C0392B',
    cursor:       'pointer',
    minHeight:    48,
    transition:   'all 0.15s ease',
  },

  // ── Professional Mode teaser ──
  proTeaserCard: {
    background:   '#FFFFFF',
    borderRadius: 16,
    padding:      '28px 24px',
    boxShadow:    '0 2px 14px rgba(27,67,50,0.07)',
    border:       '1.5px solid #EFE7D7',
    display:      'flex',
    flexDirection:'column',
    alignItems:   'center',
    textAlign:    'center',
    gap:          16,
  },
  proTeaserIcon: {
    fontSize:     '2.8rem',
    lineHeight:   1,
  },
  proTeaserDesc: {
    fontSize:   '1rem',
    fontWeight: 500,
    color:      '#5A4A3A',
    lineHeight: 1.6,
    maxWidth:   420,
    margin:     0,
  },
  proTeaserBtn: {
    display:      'inline-flex',
    alignItems:   'center',
    padding:      '14px 28px',
    background:   'linear-gradient(135deg, #1B4332, #2D6A4F)',
    color:        '#FFFFFF',
    fontFamily:   'Nunito, sans-serif',
    fontSize:     '1rem',
    fontWeight:   800,
    border:       'none',
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    52,
    boxShadow:    '0 4px 16px rgba(27,67,50,0.22)',
  },

  // ── Help form ──
  helpForm: {
    display:       'flex',
    flexDirection: 'column' as const,
    gap:           16,
    padding:       '16px 22px 20px',
  },
  helpField: {
    display:       'flex',
    flexDirection: 'column' as const,
    gap:           6,
  },
  helpLabel: {
    fontSize: '0.875rem',
    fontWeight:    700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color:         '#9A8070',
  },
  helpInput: {
    fontFamily:   'Nunito, sans-serif',
    fontSize:     '1rem',
    padding:      '12px 14px',
    borderRadius: 10,
    border:       '1.5px solid #EFE7D7',
    background:   '#FAF6EF',
    outline:      'none',
    color:        '#2E1F14',
    minHeight:    48,
    width:        '100%',
    boxSizing:    'border-box' as const,
  },
  helpTextarea: {
    fontFamily:   'Nunito, sans-serif',
    fontSize:     '1rem',
    padding:      '12px 14px',
    borderRadius: 10,
    border:       '1.5px solid #EFE7D7',
    background:   '#FAF6EF',
    outline:      'none',
    color:        '#2E1F14',
    width:        '100%',
    boxSizing:    'border-box' as const,
    resize:       'vertical' as const,
    minHeight:    120,
    lineHeight:   1.6,
  },
  helpError: {
    fontSize:   '0.9rem',
    color:      '#C0392B',
    fontWeight: 600,
    margin:     0,
  },
  helpSuccess: {
    padding:    '24px 22px',
    fontSize:   '1.05rem',
    fontWeight: 600,
    color:      '#1B4332',
    lineHeight: 1.6,
  },

  // ── Language ──
  langCard: {
    display: 'flex',
    gap:     12,
    flexWrap: 'wrap',
  },
  langBtn: {
    display:       'flex',
    alignItems:    'center',
    gap:           8,
    fontSize:      '1rem',
    fontWeight:    600,
    color:         '#2E1F14',
    background:    '#FFFFFF',
    border:        '1.5px solid #EFE7D7',
    borderRadius:  12,
    padding:       '14px 24px',
    cursor:        'pointer',
    minHeight:     52,
    boxShadow:     '0 2px 10px rgba(27,67,50,0.06)',
    transition:    'all 0.15s ease',
  },
  langBtnActive: {
    background:   '#F0FAF4',
    border:       '1.5px solid #52B788',
    color:        '#1B4332',
    fontWeight:   800,
  },
  langCheck: {
    fontSize:   '0.9rem',
    color:      '#52B788',
    fontWeight: 800,
  },

  // ── Sessions expand ──
  showMoreBtn: {
    display:      'flex',
    justifyContent: 'center',
    alignItems:   'center',
    width:        '100%',
    padding:      '12px',
    background:   '#FFFFFF',
    border:       '1.5px dashed #D5C9B5',
    borderRadius: 14,
    fontSize:     '1.3rem',
    fontWeight:   700,
    color:        '#9A8070',
    cursor:       'pointer',
    letterSpacing: '0.1em',
    minHeight:    48,
  },

  // ── Help trigger ──
  helpTriggerRow: {
    padding:       '18px 22px',
    display:       'flex',
    flexDirection: 'column' as const,
    gap:           12,
  },
  helpTriggerDesc: {
    fontSize:   '0.95rem',
    color:      '#5A4A3A',
    lineHeight: 1.6,
    margin:     0,
  },

  // ── Modal ──
  modalOverlay: {
    position:       'fixed' as const,
    inset:          0,
    background:     'rgba(27,67,50,0.45)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         1000,
    padding:        '24px 16px',
  },
  modalBox: {
    background:   '#FFFFFF',
    borderRadius: 20,
    width:        '100%',
    maxWidth:     480,
    maxHeight:    '90vh',
    overflowY:    'auto' as const,
    boxShadow:    '0 20px 60px rgba(27,67,50,0.25)',
  },
  modalHeader: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '20px 22px 16px',
    borderBottom:    '1.5px solid #EFE7D7',
  },
  modalTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '1.6rem',
    fontWeight: 700,
    color:      '#1B4332',
    margin:     0,
  },
  modalCloseBtn: {
    background:   'none',
    border:       'none',
    fontSize:     '1.1rem',
    color:        '#9A8070',
    cursor:       'pointer',
    minWidth:     36,
    minHeight:    36,
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
}
